import React, { useCallback } from "react";

import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  UseFieldArrayRemove,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { Card } from "src/base-components/Card";
import { EditorTable } from "src/base-components/EditorTable/EditorTable";
import { EditorTableStoreProvider } from "src/base-components/EditorTable/StoreProvider";
import { Button } from "src/design-system/Button";
import { IconButton } from "src/design-system/Icon";
import { Input } from "src/design-system/Input";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { columnGroups } from "src/scorecardNode/columnsConfig";
import { FactorContextProvider } from "src/scorecardNode/context";
import { getDefaultCase } from "src/scorecardNode/defaultValues";
import { FactorError, FormShape } from "src/scorecardNode/types";
import { transformToTableData } from "src/scorecardNode/utils";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

type Props = {
  index: number;
  totalFactors: number;
  errors: Nullable<FactorError>;
  onRemove: UseFieldArrayRemove;
  readonly: boolean;
};

type AddCaseButtonProps = {
  factorIndex: number;
  readonly: boolean;
};
const AddCaseButton: React.FC<AddCaseButtonProps> = ({
  readonly,
  factorIndex,
}) => {
  const { control } = useFormContext<FormShape>();
  const { append: appendCase, fields: cases } = useFieldArray({
    control,
    name: `factors.${factorIndex}.cases`,
  });

  const handleAddCase = useCallback(() => {
    const lastCase = cases[cases.length - 1];
    appendCase(getDefaultCase({ operator: lastCase?.predicate?.operator }));
  }, [cases, appendCase]);

  return (
    <Button
      dataLoc={`scorecard-factor-${factorIndex}-add-case`}
      isDisabled={readonly}
      iconLeft={faPlus}
      size="sm"
      variant="secondary"
      onClick={handleAddCase}
    >
      Add case
    </Button>
  );
};

export const Factor: React.FC<Props> = ({
  totalFactors,
  errors,
  index,
  onRemove,
  readonly,
}) => {
  const { control, register, watch, getValues } = useFormContext<FormShape>();
  const equalWeights = useWatch<FormShape, "equal_weights">({
    name: "equal_weights",
  });
  const extractScores = useWatch<FormShape, "extract_scores">({
    name: "extract_scores",
  });

  const outputFieldId = getValues(`factors.${index}.output_field.id`);
  const isOutputFieldHighlighted = useIsFieldHighlighted(outputFieldId);
  const [highlightRef] = useHighlightedScrollIntoView(isOutputFieldHighlighted);

  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <Controller
      control={control}
      name={`factors.${index}`}
      render={({ field }) => {
        return (
          <Card key={field.value.id} data-loc={`scorecard-factor-${index}`}>
            <Card.Header className="mb-3">
              <Card.Title
                dataLoc={`scorecard-factor-${index}-title`}
                title={field.value.name}
              />
              <IconButton
                disabled={readonly}
                icon={faTrashCan}
                size="xs"
                onClick={handleRemove}
              />
            </Card.Header>
            <Card.Content>
              <FactorContextProvider
                equalWeights={equalWeights}
                field={field}
                index={index}
                readonly={readonly}
                totalFactors={totalFactors}
              >
                <div className="decideScrollbar w-full">
                  <EditorTableStoreProvider>
                    <EditorTable
                      columnGroups={columnGroups}
                      data={transformToTableData(watch(`factors.${index}`))}
                      dataLoc={`scorecard-factor-${index}-table`}
                      errors={errors?.tableErrors}
                      readonly={readonly}
                      readonlyCells={[
                        { col: "predicate", row: field.value.fallback.id },
                      ]}
                      hideHeaders
                    />
                  </EditorTableStoreProvider>
                </div>
              </FactorContextProvider>
              {extractScores && (
                <div className="flex w-full items-center gap-x-4">
                  <p className="font-inter-normal-12px text-gray-700">
                    Assign unweighted {field.value.name} score to
                  </p>
                  <div ref={highlightRef} className="grow">
                    <Input
                      dataLoc="scorecard-assign-unweighted-score-input"
                      error={Boolean(errors?.outputFieldError)}
                      highlighted={isOutputFieldHighlighted}
                      placeholder="scores.credit_score"
                      prefixText="data."
                      readOnly={readonly}
                      variant="monospaced"
                      {...register(`factors.${index}.output_field.value`)}
                    />
                  </div>
                </div>
              )}
              <AddCaseButton factorIndex={index} readonly={readonly} />
            </Card.Content>
          </Card>
        );
      }}
    />
  );
};
